/**
 * 606 Meta5 AI Trading - Positions Tab
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Loader2 } from 'lucide-react';
import { useTradingContext } from '../../context/TradingContext';
import { useTrading } from '../../lib/trading-i18n';
import { closePosition } from '../../lib/trading-api';
import PnL from '../../components/trading/PnL';
import Badge from '../../components/trading/Badge';
import { Button } from '../../components/ui/button';

const PositionsTab = ({ language = 'ar' }) => {
  const t = useTrading(language);
  const { positions, refreshCore } = useTradingContext();
  const [closingTicket, setClosingTicket] = useState(null);

  const handleClose = async (ticket) => {
    setClosingTicket(ticket);
    try {
      await closePosition(ticket);
      await refreshCore();
    } catch (err) {
      console.error('Failed to close position:', err);
    }
    setClosingTicket(null);
  };

  const formatTime = (isoString) => {
    try {
      return new Date(isoString).toLocaleString(language === 'ar' ? 'ar-AE' : 'en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '--';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-4"
    >
      <h3 className="text-lg font-semibold text-white mb-4">{t.openPositions}</h3>
      
      {positions.length === 0 ? (
        <p className="text-[#71717A] text-center py-12">{t.noOpenPositions}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-right py-3 px-2 text-xs font-medium text-[#71717A]">{t.symbol}</th>
                <th className="text-center py-3 px-2 text-xs font-medium text-[#71717A]">{t.type}</th>
                <th className="text-center py-3 px-2 text-xs font-medium text-[#71717A]">{t.volume}</th>
                <th className="text-center py-3 px-2 text-xs font-medium text-[#71717A]">{t.openPrice}</th>
                <th className="text-center py-3 px-2 text-xs font-medium text-[#71717A]">{t.currentPrice}</th>
                <th className="text-center py-3 px-2 text-xs font-medium text-red-400/80">{t.stopLoss}</th>
                <th className="text-center py-3 px-2 text-xs font-medium text-emerald-400/80">{t.takeProfit}</th>
                <th className="text-center py-3 px-2 text-xs font-medium text-[#71717A]">{t.profit}</th>
                <th className="text-center py-3 px-2 text-xs font-medium text-[#71717A]">{t.openTime}</th>
                <th className="text-center py-3 px-2 text-xs font-medium text-[#71717A]"></th>
              </tr>
            </thead>
            <tbody>
              {positions.map((pos) => (
                <tr key={pos.ticket} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="py-3 px-2">
                    <span className="text-white font-medium">{pos.symbol}</span>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <Badge variant={pos.type}>{pos.type === 'buy' ? t.buy : t.sell}</Badge>
                  </td>
                  <td className="py-3 px-2 text-center text-white">{pos.volume}</td>
                  <td className="py-3 px-2 text-center font-mono text-white">{pos.open_price?.toFixed(5)}</td>
                  <td className="py-3 px-2 text-center font-mono text-white">{pos.current_price?.toFixed(5)}</td>
                  <td className="py-3 px-2 text-center font-mono text-red-400">{pos.sl?.toFixed(5) || '--'}</td>
                  <td className="py-3 px-2 text-center font-mono text-emerald-400">{pos.tp?.toFixed(5) || '--'}</td>
                  <td className="py-3 px-2 text-center">
                    <PnL value={pos.profit || 0} size="sm" />
                  </td>
                  <td className="py-3 px-2 text-center text-[#71717A] text-xs">{formatTime(pos.open_time)}</td>
                  <td className="py-3 px-2 text-center">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleClose(pos.ticket)}
                      disabled={closingTicket === pos.ticket}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      data-testid={`close-position-${pos.ticket}`}
                    >
                      {closingTicket === pos.ticket ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <X className="h-4 w-4" />
                      )}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
};

export default PositionsTab;
